import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

export default function ImportCSV({ onClose, onImportComplete, addressBookId }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [step, setStep] = useState('upload');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const fieldOptions = [
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'street', label: 'Street Address' },
    { value: 'city', label: 'City' },
    { value: 'state', label: 'State' },
    { value: 'zip', label: 'Zip Code' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'so_name', label: 'Significant Other Name' },
    { value: 'so_phone', label: 'SO Phone' },
    { value: 'so_birthday', label: 'SO Birthday' },
    { value: 'ignore', label: '-- Ignore this column --' }
  ];

  function detectColumnMapping(headerRow) {
    const mapping = {};
    
    headerRow.forEach(header => {
      const normalized = header.toLowerCase().trim();
      
      if (normalized.match(/first.*name|given.*name|fname/)) {
        mapping[header] = 'first_name';
      }
      else if (normalized.match(/last.*name|family.*name|surname|lname/)) {
        mapping[header] = 'last_name';
      }
      else if (normalized.match(/email|e-mail|mail/)) {
        mapping[header] = 'email';
      }
      else if (normalized.match(/phone|tel|mobile|cell/)) {
        mapping[header] = 'phone';
      }
      else if (normalized.match(/street|address.*1|addr/)) {
        mapping[header] = 'street';
      }
      else if (normalized.match(/city|town/)) {
        mapping[header] = 'city';
      }
      else if (normalized.match(/state|province/)) {
        mapping[header] = 'state';
      }
      else if (normalized.match(/zip|postal/)) {
        mapping[header] = 'zip';
      }
      else if (normalized.match(/birth.*day|dob|birthday/)) {
        mapping[header] = 'birthday';
      }
      else if (normalized.match(/spouse|partner|significant.*other|so.*name/)) {
        mapping[header] = 'so_name';
      }
      else {
        mapping[header] = 'ignore';
      }
    });
    
    return mapping;
  }

  function parseBirthdayToISO(dateString) {
    if (!dateString || dateString.trim() === '') return null;
    
    const cleaned = dateString.trim();
    
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      return cleaned;
    }
    
    const monthNameMatch = cleaned.match(/^([a-zA-Z]+)\s+(\d{1,2})$/);
    if (monthNameMatch) {
      const monthNames = {
        'january': '01', 'jan': '01',
        'february': '02', 'feb': '02',
        'march': '03', 'mar': '03',
        'april': '04', 'apr': '04',
        'may': '05',
        'june': '06', 'jun': '06',
        'july': '07', 'jul': '07',
        'august': '08', 'aug': '08',
        'september': '09', 'sep': '09', 'sept': '09',
        'october': '10', 'oct': '10',
        'november': '11', 'nov': '11',
        'december': '12', 'dec': '12'
      };
      
      const monthName = monthNameMatch[1].toLowerCase();
      const day = monthNameMatch[2].padStart(2, '0');
      const monthNum = monthNames[monthName];
      
      if (monthNum) {
        return `2024-${monthNum}-${day}`;
      }
    }
    
    const numericMatch = cleaned.match(/^(\d{1,2})[\/\-](\d{1,2})$/);
    if (numericMatch) {
      const month = numericMatch[1].padStart(2, '0');
      const day = numericMatch[2].padStart(2, '0');
      return `2024-${month}-${day}`;
    }
    
    const fullDateMatch = cleaned.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (fullDateMatch) {
      const month = fullDateMatch[1].padStart(2, '0');
      const day = fullDateMatch[2].padStart(2, '0');
      const year = fullDateMatch[3];
      return `${year}-${month}-${day}`;
    }
    
    const isoSlashMatch = cleaned.match(/^(\d{4})[\/](\d{1,2})[\/](\d{1,2})$/);
    if (isoSlashMatch) {
      const year = isoSlashMatch[1];
      const month = isoSlashMatch[2].padStart(2, '0');
      const day = isoSlashMatch[3].padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    const excelNumber = parseInt(cleaned);
    if (!isNaN(excelNumber) && excelNumber > 0 && excelNumber < 60000) {
      const excelEpoch = new Date(1899, 11, 30);
      const jsDate = new Date(excelEpoch.getTime() + excelNumber * 86400000);
      
      const year = jsDate.getFullYear();
      const month = String(jsDate.getMonth() + 1).padStart(2, '0');
      const day = String(jsDate.getDate()).padStart(2, '0');
      
      if (year >= 1900 && year <= 2024) {
        return `${year}-${month}-${day}`;
      } else {
        return `2024-${month}-${day}`;
      }
    }
    
    try {
      const parsed = new Date(cleaned);
      if (!isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        
        if (year >= 1900 && year <= 2024) {
          return `${year}-${month}-${day}`;
        } else {
          return `2024-${month}-${day}`;
        }
      }
    } catch (e) {
      // Parsing failed
    }
    
    console.warn('Could not parse birthday:', dateString);
    return null;
  }

  function handleFileUpload(event) {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      parseCSV(text);
    };
    reader.readAsText(uploadedFile);
  }

  function parseCSV(text) {
    const lines = text.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      alert('CSV file appears to be empty or invalid');
      return;
    }

    const headerRow = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    setHeaders(headerRow);

    const detectedMapping = detectColumnMapping(headerRow);
    setColumnMapping(detectedMapping);

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const row = {};
      headerRow.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }

    setParsedData(rows);
    setStep('preview');
  }

  function getPreviewData() {
    if (!parsedData || parsedData.length === 0) return [];
    
    return parsedData.slice(0, 5).map(row => {
      const mapped = {};
      headers.forEach(header => {
        const fieldName = columnMapping[header];
        if (fieldName && fieldName !== 'ignore') {
          mapped[fieldName] = row[header];
        }
      });
      return mapped;
    });
  }

  async function handleImport() {
    if (!parsedData || parsedData.length === 0) {
      alert('No data to import');
      return;
    }

    const hasFirstName = Object.values(columnMapping).includes('first_name');
    const hasLastName = Object.values(columnMapping).includes('last_name');

    if (!hasFirstName || !hasLastName) {
      alert('Please map at least First Name and Last Name columns');
      return;
    }

    setImporting(true);
    setStep('importing');

    try {
      const contactsToInsert = parsedData.map(row => {
        const contact = {
          address_book_id: addressBookId,
          submitted_at: new Date().toISOString()
        };

        headers.forEach(header => {
          const fieldName = columnMapping[header];
          const value = row[header];

          if (fieldName && fieldName !== 'ignore' && value) {
            switch (fieldName) {
              case 'first_name':
                contact.first_name = value;
                break;
              case 'last_name':
                contact.last_name = value;
                break;
              case 'email':
                contact.email = value;
                break;
              case 'phone':
                contact.phone = value;
                break;
              case 'street':
                contact.street = value;
                break;
              case 'city':
                contact.city = value;
                break;
              case 'state':
                contact.state = value;
                break;
              case 'zip':
                contact.zip_code = value;
                break;
              case 'birthday':
                contact.birthday = parseBirthdayToISO(value);
                break;
              case 'so_name':
                contact.so_name = value;
                break;
              case 'so_phone':
                contact.so_phone = value;
                break;
              case 'so_birthday':
                contact.so_birthday = parseBirthdayToISO(value);
                break;
              default:
                break;
            }
          }
        });

        return contact;
      }).filter(c => c.first_name && c.last_name);

      const batchSize = 100;
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < contactsToInsert.length; i += batchSize) {
        const batch = contactsToInsert.slice(i, i + batchSize);
        
        const { data, error } = await supabase
          .from('contacts')
          .insert(batch);

        if (error) {
          console.error('Error importing batch:', error);
          errorCount += batch.length;
        } else {
          successCount += batch.length;
        }
      }

      setImportResults({
        total: parsedData.length,
        success: successCount,
        errors: errorCount,
        skipped: parsedData.length - contactsToInsert.length
      });

      setStep('complete');
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing contacts: ' + error.message);
      setImporting(false);
    }
  }

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{ 
        background: 'white', 
        padding: '30px', 
        borderRadius: '12px', 
        maxWidth: '800px', 
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>
          📤 Import Contacts from CSV
        </h2>

        {step === 'upload' && (
          <>
            <div style={{ 
              border: '2px dashed #e2e8f0', 
              borderRadius: '8px', 
              padding: '40px', 
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📁</div>
              <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '20px' }}>
                Upload a CSV file with your contacts
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                style={{ 
                  padding: '12px 24px', 
                  background: '#3b82f6', 
                  color: 'white', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  display: 'inline-block',
                  fontWeight: '500'
                }}
              >
                Choose CSV File
              </label>
            </div>

            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                💡 CSV Format Tips:
              </div>
              <ul style={{ fontSize: '13px', color: '#64748b', marginLeft: '20px' }}>
                <li>First row should contain column headers</li>
                <li>Common headers like "First Name", "Email", "Phone" will be auto-detected</li>
                <li>At minimum, include First Name and Last Name columns</li>
              </ul>
            </div>

            <button
              onClick={onClose}
              style={{ 
                width: '100%', 
                padding: '12px', 
                background: '#64748b', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </>
        )}

        {step === 'preview' && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '15px' }}>
                Found <strong>{parsedData.length} contacts</strong> in your CSV. Review the column mapping below:
              </p>

              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>
                  Column Mapping:
                </div>
                {headers.map(header => (
                  <div key={header} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ flex: 1, fontSize: '13px', color: '#64748b' }}>
                      "{header}" →
                    </div>
                    <select
                      value={columnMapping[header] || 'ignore'}
                      onChange={(e) => setColumnMapping({...columnMapping, [header]: e.target.value})}
                      style={{ 
                        flex: 1, 
                        padding: '8px', 
                        border: '2px solid #e2e8f0', 
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    >
                      {fieldOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>
                  Preview (first 5 rows):
                </div>
                <div style={{ overflow: 'auto' }}>
                  {getPreviewData().map((contact, idx) => (
                    <div key={idx} style={{ 
                      fontSize: '12px', 
                      color: '#64748b', 
                      padding: '8px',
                      background: 'white',
                      borderRadius: '4px',
                      marginBottom: '6px'
                    }}>
                      {contact.first_name} {contact.last_name}
                      {contact.email && ` • ${contact.email}`}
                      {contact.phone && ` • ${contact.phone}`}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setStep('upload')}
                style={{ 
                  flex: 1,
                  padding: '12px', 
                  background: '#64748b', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                style={{ 
                  flex: 1,
                  padding: '12px', 
                  background: importing ? '#94a3b8' : '#10b981', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: importing ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Import {parsedData.length} Contacts
              </button>
            </div>
          </>
        )}

        {step === 'importing' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '10px' }}>
              Importing contacts...
            </div>
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              Please wait while we add your contacts to the database.
            </p>
          </div>
        )}

        {step === 'complete' && importResults && (
          <>
            <div style={{ textAlign: 'center', padding: '20px', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981', marginBottom: '15px' }}>
                Import Complete!
              </div>
              
              <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', textAlign: 'left' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                  <strong style={{ color: '#10b981' }}>{importResults.success}</strong> contacts imported successfully
                </div>
                {importResults.errors > 0 && (
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                    <strong style={{ color: '#ef4444' }}>{importResults.errors}</strong> contacts failed to import
                  </div>
                )}
                {importResults.skipped > 0 && (
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    <strong style={{ color: '#f59e0b' }}>{importResults.skipped}</strong> contacts skipped (missing name)
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => {
                onImportComplete();
                onClose();
              }}
              style={{ 
                width: '100%',
                padding: '12px', 
                background: '#10b981', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
